import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ResourcedEmoji } from '@atlaskit/emoji';
import { Box, xcss } from '@atlaskit/primitives';
import { createAndFireSafe, createReactionClickedEvent, createReactionFocusedEvent, createReactionHoveredEvent } from '../../analytics';
import { Counter } from '../Counter';
import { ReactionParticleEffect } from '../ReactionParticleEffect';
import { ReactionTooltip } from '../ReactionTooltip';
import { messages } from '../../shared/i18n';
import { isLeftClick } from '../../shared/utils';
import { emojiStyle, emojiNoReactionStyle } from './styles';
import { ReactionButton } from './ReactionButton';

/**
 * Test id for Reaction item wrapper div
 */
export var RENDER_REACTION_TESTID = 'render_reaction_wrapper';
var containerStyles = xcss({
  position: 'relative'
});
var reactedStyles = xcss({
  backgroundColor: 'color.background.selected',
  borderColor: 'color.border.selected',
  ':hover': {
    backgroundColor: 'color.background.selected.hovered'
  },
  ':active': {
    backgroundColor: 'color.background.selected.pressed'
  }
});
var opaqueBackgroundStyles = xcss({
  backgroundColor: 'elevation.surface',
  ':hover': {
    backgroundColor: 'elevation.surface.hovered'
  },
  ':active': {
    backgroundColor: 'elevation.surface.pressed'
  }
});

/**
 * Render an emoji reaction button
 */
export var Reaction = function Reaction(_ref) {
  var emojiProvider = _ref.emojiProvider,
    onClick = _ref.onClick,
    reaction = _ref.reaction,
    _ref$onMouseEnter = _ref.onMouseEnter,
    onMouseEnter = _ref$onMouseEnter === void 0 ? function () {} : _ref$onMouseEnter,
    _ref$onFocused = _ref.onFocused,
    onFocused = _ref$onFocused === void 0 ? function () {} : _ref$onFocused,
    _ref$flash = _ref.flash,
    flash = _ref$flash === void 0 ? false : _ref$flash,
    _ref$showParticleEffe = _ref.showParticleEffect,
    showParticleEffect = _ref$showParticleEffe === void 0 ? false : _ref$showParticleEffe,
    _ref$showOpaqueBackgr = _ref.showOpaqueBackground,
    showOpaqueBackground = _ref$showOpaqueBackgr === void 0 ? false : _ref$showOpaqueBackgr;
  var intl = useIntl();
  var hoverStart = useRef();
  var focusStart = useRef();
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    emojiName = _useState2[0],
    setEmojiName = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    isTooltipEnabled = _useState4[0],
    setIsTooltipEnabled = _useState4[1];
  var reacted = reaction.reacted;
  var emojiId = {
    id: reaction.emojiId,
    shortName: ''
  };

  // TODO: Extract the flow to a custom hook to retrieve emoji detailed description from an id using a custom hook. This will benefit a better optimization instead of the emojiProvider resolving everytime.
  // Also optimize in future version to fetch in batch several emojiIds
  useEffect(function () {
    _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var emojiResource, foundEmoji;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return Promise.resolve(emojiProvider);
          case 2:
            emojiResource = _context.sent;
            _context.next = 5;
            return emojiResource.findById(reaction.emojiId);
          case 5:
            foundEmoji = _context.sent;
            if (foundEmoji) {
              setEmojiName(foundEmoji.name);
            }
          case 7:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }))();
  }, [emojiProvider, reaction.emojiId]);
  var handleClick = useCallback(function (event) {
    event.preventDefault();
    if (isLeftClick(event)) {
      var _reacted = reaction.reacted,
        _emojiId = reaction.emojiId;
      createAndFireSafe(createAnalyticsEvent, createReactionClickedEvent, !_reacted, _emojiId);
      onClick(reaction.emojiId, event);
    }
  }, [createAnalyticsEvent, reaction, onClick]);
  var handleMouseEnter = useCallback(function (event) {
    event.preventDefault();
    setIsTooltipEnabled(true);
    if (!reaction.users || !reaction.users.length) {
      focusStart.current = Date.now();
    }
    createAndFireSafe(createAnalyticsEvent, createReactionHoveredEvent, focusStart.current);
    onMouseEnter(reaction.emojiId, event);
  }, [createAnalyticsEvent, reaction, onMouseEnter]);
  var handleFocused = useCallback(function (event) {
    event.preventDefault();
    setIsTooltipEnabled(true);
    if (!reaction.users || !reaction.users.length) {
      hoverStart.current = Date.now();
    }
    createAndFireSafe(createAnalyticsEvent, createReactionFocusedEvent, hoverStart.current);
    onFocused(reaction.emojiId, event);
  }, [createAnalyticsEvent, reaction, onFocused]);
  var buttonStyles = showOpaqueBackground ? [opaqueBackgroundStyles] : [];
  return jsx(Box, {
    xcss: containerStyles
  }, showParticleEffect && jsx(ReactionParticleEffect, {
    emojiId: emojiId,
    emojiProvider: emojiProvider
  }), jsx(ReactionTooltip, {
    emojiName: emojiName,
    reaction: reaction,
    isEnabled: isTooltipEnabled
  }, jsx(ReactionButton, {
    onClick: handleClick,
    flash: flash,
    additionalStyles: reaction.reacted ? [reactedStyles] : buttonStyles,
    ariaLabel: intl.formatMessage(messages.reactWithEmoji, {
      emoji: emojiName
    }),
    ariaPressed: reacted,
    onMouseEnter: handleMouseEnter,
    onFocus: handleFocused,
    testId: RENDER_REACTION_TESTID,
    dataAttributes: {
      'data-emoji-id': reaction.emojiId,
      'data-emoji-button-id': reaction.emojiId
    }
  }, jsx("div", {
    css: [emojiStyle, reaction.count === 0 && emojiNoReactionStyle]
  }, jsx(ResourcedEmoji, {
    emojiProvider: emojiProvider,
    emojiId: emojiId,
    fitToHeight: 16
  })), jsx(Counter, {
    value: reaction.count,
    highlight: reaction.reacted
  }))));
};